/**
 * Un estado financiero con detalle expandible.
 *
 * En modo «Formato oficial» NO se dibuja nada nuevo: se renderiza exactamente
 * lo que el reporte renderizaba antes, con el mismo código. La expansión es
 * aditiva. Esa separación es deliberada: el documento que el contador firma no
 * puede cambiar de forma porque hayamos añadido una función de auditoría.
 *
 * Al subir el detalle se cambia al árbol. Las dos columnas se conservan MIENTRAS
 * el detalle sea de grupos: ahí caben y el estado se sigue leyendo enfrentado,
 * que es su forma. En cuentas no: el ESF real trae 102 cuentas, y 171 de sus
 * pares cuenta-renglón cuelgan de las dos líneas de resultado del PATRIMONIO
 * —el resultado del ejercicio ESTÁ compuesto de costos e ingresos—, así que la
 * columna derecha crece muchísimo más que la izquierda. Meterlas en dos tablas
 * de ~800 px deja el estado ilegible y desbalanceado; a esa profundidad el
 * documento pasa a ser una lista y quiere el ancho entero.
 */

import { useState } from "react";
import { api } from "../api/client";
import { useAsync } from "../api/useAsync";
import type { ReportTree, TreeNode } from "../api/types";
import { DetailToolbar } from "./DetailToolbar";
import { EntriesDrawer } from "./EntriesDrawer";
import { ErrorState, Loading } from "./States";
import { TreeTable } from "./TreeTable";

/**
 * Las columnas del árbol de un estado.
 *
 * Un estado enseña el movimiento del período y la medida que ESE estado lee —el
 * saldo en un balance, el propio movimiento en un resultado—. Cuando las dos
 * coinciden se pinta una, no la misma cifra dos veces bajo dos rótulos.
 */
function columnasDeEstado(field: "presented_closing_amount" | "presented_period_amount") {
  const movimiento = { field: "presented_period_amount" as const, label: "Movimiento" };
  if (field === "presented_period_amount") return [movimiento];
  return [movimiento, { field, label: "Saldo" }];
}
import { expandToAccounts } from "./treeNodes";
import type { Period } from "./period";
import { useTreeState } from "./useTreeState";

interface Props {
  reportKey: string;
  period: Period;
  /** Lo que se muestra en modo oficial: el estado tal como se firma. */
  official: React.ReactNode;
  /** Si el ESF se parte en dos columnas al expandirse. */
  twoColumn?: boolean;
  columnTitles?: [string, string];
  field?: "presented_closing_amount" | "presented_period_amount";
  mayReadJournal?: boolean;
}

export function ExpandableStatement({
  reportKey,
  period,
  official,
  twoColumn = false,
  columnTitles = ["ACTIVO", "PASIVO Y PATRIMONIO"],
  field = "presented_closing_amount",
  mayReadJournal = false,
}: Props) {
  const estado = useTreeState(`${reportKey}:${period.periodFrom}:${period.periodTo}`);
  const [nodoAbierto, setNodoAbierto] = useState<TreeNode | null>(null);

  const oficial = estado.mode === "formal";

  // En modo oficial no se pide nada: el estado ya está en el paquete de cierre,
  // y traer el árbol para no mostrarlo sería trabajo puro de más.
  const { data, error, loading } = useAsync<ReportTree | null>(
    () =>
      oficial
        ? Promise.resolve(null)
        : api.reportTree(reportKey, {
            mode: estado.mode,
            fullOdooChain: estado.fullChain,
            showZero: estado.showZero,
            periodFrom: period.periodFrom,
            periodTo: period.periodTo,
          }),
    [
      reportKey,
      oficial,
      estado.mode,
      estado.fullChain,
      estado.showZero,
      period.periodFrom,
      period.periodTo,
    ],
  );

  const tree = data ?? null;
  const nodos = tree?.nodes ?? [];
  // El enfrentamiento ACTIVO | PASIVO Y PATRIMONIO es la forma del documento y
  // se conserva; lo que cambia con la profundidad es si caben lado a lado.
  const dosColumnas = twoColumn && estado.mode !== "accounts";

  return (
    <>
      <DetailToolbar
        mode={estado.mode}
        onMode={estado.setMode}
        fullChain={estado.fullChain}
        onFullChain={estado.setFullChain}
        showZero={estado.showZero}
        onShowZero={estado.setShowZero}
        onExpand={() => estado.expandAll(expandToAccounts(nodos))}
        onCollapse={estado.collapseAll}
      />

      {oficial && official}

      {!oficial && loading && <Loading what="el detalle por cuenta" />}
      {!oficial && error && <ErrorState error={error} />}

      {!oficial && tree && !loading && (
        <>
          {dosColumnas ? (
            <div className="card-columns">
              <div className="card">
                <h2>{columnTitles[0]}</h2>
                <TreeTable
                  nodes={nodos.filter((n) => n.side === "left")}
                  expanded={estado.expanded}
                  onToggle={estado.toggle}
                  onOpenEntries={mayReadJournal ? setNodoAbierto : undefined}
                  moneyColumns={columnasDeEstado(field)}
                />
              </div>
              <div className="card">
                <h2>{columnTitles[1]}</h2>
                <TreeTable
                  nodes={nodos.filter((n) => n.side === "right")}
                  expanded={estado.expanded}
                  onToggle={estado.toggle}
                  onOpenEntries={mayReadJournal ? setNodoAbierto : undefined}
                  moneyColumns={columnasDeEstado(field)}
                />
              </div>
            </div>
          ) : twoColumn ? (
            // Una columna, pero los dos lados siguen separados y rotulados: el
            // estado no deja de ser un ESF por mostrarse en vertical.
            <>
              {(["left", "right"] as const).map((lado, indice) => {
                const filas = nodos.filter((n) => n.side === lado);
                if (filas.length === 0) return null;
                return (
                  <div className="card" key={lado}>
                    <h2>{columnTitles[indice]}</h2>
                    <TreeTable
                      nodes={filas}
                      expanded={estado.expanded}
                      onToggle={estado.toggle}
                      onOpenEntries={mayReadJournal ? setNodoAbierto : undefined}
                      moneyColumns={columnasDeEstado(field)}
                    />
                  </div>
                );
              })}
            </>
          ) : (
            <div className="card">
              <TreeTable
                nodes={nodos}
                expanded={estado.expanded}
                onToggle={estado.toggle}
                onOpenEntries={mayReadJournal ? setNodoAbierto : undefined}
                moneyColumns={columnasDeEstado(field)}
              />
            </div>
          )}

          <EntriesDrawer
            reportKey={reportKey}
            node={nodoAbierto}
            period={period}
            onClose={() => setNodoAbierto(null)}
          />
        </>
      )}
    </>
  );
}
